import { AppIcon } from '@/components/ui/app-icon';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { iconMinus, iconPlus } from '@/lib/icons';
import { cn } from '@/lib/utils';

export const MAX_DAILY_LIMIT = 1000;

/**
 * A mailbox sending more than this a day is far past what mailbox providers
 * and recipients tolerate: the stepper marks the number instead of letting a
 * stray extra zero pass quietly.
 */
export const DAILY_LIMIT_WARN = 200;

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.round(value)));
}

/**
 * Daily sending limit control: minus, an editable number, plus. Buttons commit
 * immediately; typing commits on blur or Enter so a half-typed number is never
 * saved.
 */
export function LimitStepper({
  value,
  onCommit,
  disabled,
  step = 5,
  min = 0,
  max = MAX_DAILY_LIMIT,
  ariaLabel = 'Daily sending limit',
  className,
  inputValue,
  onInputValueChange,
  warnAbove,
}: {
  value: number;
  onCommit: (next: number) => void;
  disabled?: boolean;
  step?: number;
  /** The least this limit may be set to. */
  min?: number;
  /** The most this limit may be set to. */
  max?: number;
  ariaLabel?: string;
  className?: string;
  /** An explicit form can retain uncommitted typing through saved refreshes. */
  inputValue?: string;
  onInputValueChange?: (value: string) => void;
  /** Above this the number reads in amber, with the reason on hover and focus. */
  warnAbove?: number;
}) {
  const [localDraft, setLocalDraft] = useState(String(value));
  const draft = inputValue ?? localDraft;
  const setDraft = (next: string) => {
    setLocalDraft(next);
    onInputValueChange?.(next);
  };
  useEffect(() => setLocalDraft(String(value)), [value]);

  const commit = (next: number) => {
    const clamped = clamp(next, min, max);
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  };
  // Step from the number on screen, not the last saved one: three quick clicks
  // must move three steps, and a typed value must not be thrown away by the
  // next click.
  const shown = draft === '' ? value : Number(draft);
  // Clearing the field is not "set the limit to zero" (which stops the address
  // sending). Put the saved number back instead.
  const commitTyped = () =>
    draft.trim() === '' ? setDraft(String(value)) : commit(Number(draft));
  const warned = warnAbove !== undefined && shown > warnAbove;
  const button =
    'h-8 w-8 rounded-none text-muted-foreground/80 hover:bg-muted/50 hover:text-foreground [&_svg]:size-3';

  return (
    <span
      className={cn(
        'inline-flex items-center overflow-hidden rounded-md border border-gray-200 bg-background dark:border-white/[0.08]',
        className
      )}
    >
      <Button
        variant='ghost'
        size='icon'
        aria-label={'Lower ' + ariaLabel.toLowerCase()}
        disabled={disabled || shown <= min}
        onClick={() => commit(shown - step)}
        className={button}
      >
        <AppIcon icon={iconMinus} />
      </Button>
      <input
        type='text'
        inputMode='numeric'
        aria-label={ariaLabel}
        title={
          warned
            ? `More than ${warnAbove} a day puts the mailbox at risk of being blocked`
            : undefined
        }
        disabled={disabled}
        value={draft}
        onChange={(event) =>
          setDraft(event.target.value.replace(/[^0-9]/g, ''))
        }
        onBlur={commitTyped}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        className={cn(
          'h-8 w-11 border-x border-gray-200 bg-transparent text-center text-xs tabular-nums outline-hidden focus:bg-primary/5 disabled:opacity-60 dark:border-white/[0.08]',
          warned && 'font-semibold text-amber-600 dark:text-amber-400'
        )}
      />
      <Button
        variant='ghost'
        size='icon'
        aria-label={'Raise ' + ariaLabel.toLowerCase()}
        disabled={disabled || shown >= max}
        onClick={() => commit(shown + step)}
        className={button}
      >
        <AppIcon icon={iconPlus} />
      </Button>
    </span>
  );
}
